import { httpGet } from '../../components/utils';
import {
    DIRECTORY_TYPES,
    ENTITY_SELECTOR_FILTER_TYPES,
    HTTP_OPTIONS,
    SEARCH_MIN_CHARS
} from '@silverfort-nodelibs/common/utils/consts';
import { AxiosResponse } from 'axios';
import { Entity } from '../types/Entity';
import { useCallback, useRef, useState } from 'react';

interface Filters {
    entitiesTypes?: number[];
    searchQuery: string;
}

type EntityDto = Omit<Entity, 'domainName' | 'isDeleted'> & {
    domain_name: string;
    is_deleted: boolean;
};

interface SearchResponse {
    Results: EntityDto[];
    requestID: string;
}

export interface useLazySearchEntitiesReturnType {
    isLoading: boolean;
    data: Entity[];
    searchEntities: ({ entitiesTypes, searchQuery }: Filters) => Promise<void>;
    resetData: () => void;
    error?: string;
}

const ENTITY_TYPES: number[] = Object.values(ENTITY_SELECTOR_FILTER_TYPES)
    .filter((type) => !!type.searchEntitiesTypes)
    .flatMap((type) => type.searchEntitiesTypes as number[]);

const convertEntityDtoToEntity = (entityDto: EntityDto): Entity => {
    return {
        ...entityDto,
        domainName: entityDto.domain_name,
        isDeleted: entityDto.is_deleted
    };
};

export const useLazySearchEntities = (): useLazySearchEntitiesReturnType => {
    const [data, setData] = useState<Entity[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>();
    const lastRequestID = useRef(-1);
    const lastSentData = useRef<Filters>({ searchQuery: '' });

    const searchEntities = useCallback(async ({ entitiesTypes, searchQuery }: Filters) => {
        try {
            setIsLoading(true);
            setError(undefined);

            let escapedQuery = searchQuery.trim();
            if (escapedQuery === '' || escapedQuery.length < SEARCH_MIN_CHARS) {
                lastSentData.current.searchQuery = '';
                setData([]);
            }

            escapedQuery = encodeURIComponent(escapedQuery);

            const isSameSearch =
                lastSentData.current.searchQuery === escapedQuery &&
                ((!entitiesTypes && !lastSentData.current.entitiesTypes) ||
                    (entitiesTypes?.length === lastSentData.current.entitiesTypes?.length &&
                        entitiesTypes?.every((type) => lastSentData.current.entitiesTypes?.includes(type))));

            if (isSameSearch) {
                return;
            }

            const newRequestID = lastRequestID.current + 1;
            const response = (await httpGet(`/users/searchEntitiesByTypes/${escapedQuery}/${newRequestID}`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: {
                        hideDeleted: true,
                        entitiesTypes: entitiesTypes?.length ? entitiesTypes : ENTITY_TYPES,
                        authTypes: [DIRECTORY_TYPES.ad.key]
                    }
                }
            })) as unknown as AxiosResponse<SearchResponse>;

            if (response.data.requestID !== newRequestID.toString()) {
                return;
            }

            const entities = response.data.Results.map(convertEntityDtoToEntity);
            setData(entities);
            lastSentData.current = { searchQuery: escapedQuery, entitiesTypes };
        } catch (error) {
            setError('Failed to search entities');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const resetData = () => {
        setData([]);
    };

    return { data, resetData, isLoading, error, searchEntities };
};
